import React, { useState, useEffect } from 'react';
import { ToggleButtonGroup, ToggleButton, Alert, Badge, Button, Table, Row, Col, Card } from "react-bootstrap";
import { Cursor, Collection, PlusCircle } from "react-bootstrap-icons";
import { getChartTitle, getChartDescription, getTeethData, formatTo2Decimals } from '../dentist/Formatter';
import { formatDisplayDate } from '../../../utils/formatter';
import { toothConditions } from '../dentist/data/teethData';
import { v4 as uuidv4 } from "uuid";
import Tooth from './Tooth';
import CustomPagination from '../../reusables/CustomPagination';
import TreatmentPlanForm from './TreatmentPlanForm';
import BulkTreatmentPlanForm from './BulkTreatmentPlanForm';
import EditBulkTreatmentForm from './EditBulkTreatmentForm';
import EditTreatmentPlanForm from './EditTreatmentPlanForm';
import AddDentalPaymentForm from './AddDentalPaymentForm';

const DentalChart = ({ dentalChartType, dentalChart, onChange }) => {
	const [selectedTooth, setSelectedTooth] = useState(null);
	const [selectedTeeth, setSelectedTeeth] = useState(new Set());
	const [chartType, setChartType] = useState("adult");
	const [treatmentPlans, setTreatmentPlans] = useState([]);
	const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);
	const [selectedTreatment, setSelectedTreatment] = useState(null);
	const [expandedPayments, setExpandedPayments] = useState({});

	const [showTreatmentForm, setShowTreatmentForm] = useState(false);
	const [showEditTreatmentForm, setShowEditTreatmentForm] = useState(false);
	const [editingPlan, setEditingPlan] = useState(null);

	const [bulkMode, setBulkMode] = useState(false);
	const [bulkPayments, setBulkPayments] = useState({});
	const [showBulkTreatmentForm, setShowBulkTreatmentForm] = useState(false);
	const [showEditBulkTreatmentForm, setShowEditBulkTreatmentForm] = useState(false);
	const [editingBulk, setEditingBulk] = useState(null);

	const [currentPage, setCurrentPage] = useState(1);
	const pageSize = parseInt(process.env.REACT_APP_PAGINATION_PAGE_SIZE);
	const amountPaidVia = { 'cash': 'Cash', 'card': 'Card', 'upi': 'UPI', 'other': 'Other' };

	// Initialize treatmentPlans from dentalChart GET response
	useEffect(() => {
		if (dentalChart && dentalChart.teeth && dentalChart.teeth.length > 0 && dentalChart.chart_type === dentalChartType) {
			const plans = [];
			dentalChart.teeth.forEach(tooth => {
				if (tooth.treatments && tooth.treatments.length > 0) {
					tooth.treatments.forEach(tr => {
						plans.push({
							...tr,
							tooth_number: tooth.tooth_number,
							isNew: tr.isNew === true ? true : false
						});
					});
				}
			});

			const bulkPaymentsMap = {};
			if (dentalChart.bulkPayments && Array.isArray(dentalChart.bulkPayments)) {
				dentalChart.bulkPayments.forEach((bp) => {
					bulkPaymentsMap[bp.bulk_group] = bp;
				});
			}

			setChartType(dentalChart.chart_type);
			setTreatmentPlans(plans);
			setBulkPayments(bulkPaymentsMap);
		} else if (!dentalChart || Object.keys(dentalChart).length === 0 || dentalChart.chart_type !== dentalChartType) {
			setChartType(dentalChartType);
			setTreatmentPlans([]);
			setBulkPayments({});
			syncParent({ plans: [] });
		}
	}, [dentalChart, dentalChartType]);

	const syncParent = (payload) => {
		if (onChange) {
			let plans = [];
			let newBulk = null;

			if (Array.isArray(payload)) {
				plans = payload;
			} else if (payload) {
				plans = payload.plans || [];
				newBulk = payload.bulkPayments || null;
			} else {
				plans = treatmentPlans;
			}

			let mergedBulk = { ...bulkPayments };
			if (newBulk) {
				if (Array.isArray(newBulk)) {
					newBulk.forEach(b => {
						mergedBulk[b.bulk_group] = b; // overwrite or add each
					});
				} else {
					mergedBulk[newBulk.bulk_group] = newBulk;
				}
			}

			const toothNumbers = [...new Set(plans.map(p => p.tooth_number))];
			const result = {
				chart_type: chartType,
				is_active: true,
				teeth: toothNumbers.map(toothNumber => ({
					tooth_number: toothNumber,
					treatments: plans
						.filter(p => p.tooth_number === toothNumber)
						.map(p => {
							const copy = { ...p };
							// delete copy.isNew;
							delete copy._uiTemp;
							return copy;
						})
				})),
				bulkPayments: Object.values(mergedBulk)
			};

			onChange(result);
		}
	};

	const handleToothClick = (tooth) => {
		const identifier = Number(tooth.number);
		if (bulkMode) {
			const newSelectedTeeth = new Set(selectedTeeth);
			if (newSelectedTeeth.has(identifier)) newSelectedTeeth.delete(identifier);
			else newSelectedTeeth.add(identifier);
			setSelectedTeeth(newSelectedTeeth);
		} else {
			setSelectedTooth(selectedTooth === identifier ? null : identifier);
			setSelectedTeeth(new Set());
		}
	};

	const toggleBulkMode = () => {
		setBulkMode(!bulkMode);
		setSelectedTooth(null);
		setSelectedTeeth(new Set());
	};

	const handleAddTreatmentPlan = () => {
		if (!selectedTooth) return;
		setShowTreatmentForm(true);
	};

	const handleBulkAddTreatmentPlan = () => {
		if (selectedTeeth.size === 0) return;
		setShowBulkTreatmentForm(true);
	};

	const handleEditTreatmentPlan = (plan, bulkPaymentData = null, teethCount = null) => {
		if (plan.bulk_group) {
			const payload = {
				plan,
				bulkPaymentData,
				teethCount: teethCount || 1
			};

			setEditingBulk(payload);
			setShowEditBulkTreatmentForm(true);
		} else {
			setEditingPlan(plan);
			setShowEditTreatmentForm(true);
		}
	};

	const handleSaveTreatmentPlan = (treatmentData) => {
		const newPlan = {
			treatment_id: uuidv4(),
			tooth_number: selectedTooth,
			...treatmentData,
			isNew: true
		};

		const updatedPlans = [...treatmentPlans, newPlan];
		setTreatmentPlans(updatedPlans);
		setShowTreatmentForm(false);
		syncParent({ plans: updatedPlans });
	};

	const handleRemoveTreatmentPlan = (treatment_id) => {
		const plan = treatmentPlans.find(p => p.treatment_id === treatment_id);
		if (!plan) return;
		if (!plan.isNew) return;

		const updatedPlans = treatmentPlans.filter(plan => plan.treatment_id !== treatment_id);
		setTreatmentPlans(updatedPlans);
		syncParent({ plans: updatedPlans });
	};

	const handleUpdateTreatmentPlan = (updatedData) => {
		const updatedPlans = treatmentPlans.map(plan =>
			plan.treatment_id === editingPlan.treatment_id ? { ...plan, ...updatedData } : plan
		);
		setTreatmentPlans(updatedPlans);
		setShowEditTreatmentForm(false);
		setEditingPlan(null);
		syncParent({ plans: updatedPlans });
	};

	const handleRemoveBulkTreatment = (bulkGroupId) => {
		const updatedPlans = treatmentPlans.filter(p => p.bulk_group !== bulkGroupId);

		const updatedBulkPayments = { ...bulkPayments };
		delete updatedBulkPayments[bulkGroupId];

		setTreatmentPlans(updatedPlans);
		setBulkPayments(updatedBulkPayments);

		syncParent({ plans: updatedPlans, bulkPayments: null });
	};

	const handleUpdateBulkTreatment = (bulkPayload) => {
		const { plans: updatedPlansForBulk, bulkPayments: updatedBulk } = bulkPayload;
		const groupId = updatedBulk.bulk_group;

		const updatedBulkPayments = {
			...bulkPayments,
			[groupId]: {
				...bulkPayments[groupId],
				...updatedBulk,
			}
		};
		const updatedPlans = treatmentPlans.map(plan => {
			const matching = updatedPlansForBulk.find(p => p.treatment_id === plan.treatment_id);
			return matching ? matching : plan;
		});

		setTreatmentPlans(updatedPlans);
		setBulkPayments(updatedBulkPayments);
		setShowEditBulkTreatmentForm(false);
		setEditingBulk(null);
		syncParent({ plans: updatedPlans, bulkPayments: updatedBulkPayments[groupId] });
	};

	const handleSaveBulkTreatmentPlan = (bulkPayload) => {
		const { plans, bulkPayments: newBulkPayments } = bulkPayload;
		const newPlans = plans.map(item => ({
			...item,
			treatment_id: uuidv4(),
			isNew: true
		}));

		const updatedPlans = [...treatmentPlans, ...newPlans];
		setTreatmentPlans(updatedPlans);

		if (newBulkPayments) {
			setBulkPayments(prev => ({
				...prev,
				[newBulkPayments.bulk_group]: {
					...newBulkPayments,
					dental_payment_history: newBulkPayments.dental_payment_history || []
				}
			}));
		}

		setShowBulkTreatmentForm(false);
		setSelectedTeeth(new Set());
		setBulkMode(false);
		syncParent({ plans: updatedPlans, bulkPayments: newBulkPayments });
	};

	const handleOpenAddPaymentForm = (treatment, bulkGroup = null) => {
		if (bulkGroup) {
			setSelectedTreatment({ ...treatment, bulk_group: bulkGroup, isBulk: true });
		} else {
			setSelectedTreatment(treatment);
		}
		setShowAddPaymentForm(true);
	};

	const handleSavePayment = (payment) => {
		if (selectedTreatment?.isBulk && selectedTreatment.bulk_group) {
			const groupId = selectedTreatment.bulk_group;
			const bulk = bulkPayments[groupId];
			if (!bulk) return;

			const prevPaid = parseFloat(bulk?.paid_amount) || 0;
			const newPayment = parseFloat(payment.amount) || 0;
			const totalPaid = formatTo2Decimals(prevPaid + newPayment);

			const totalFinalCost = formatTo2Decimals(parseFloat(bulk?.final_cost) || 0);
			const totalRemaining = formatTo2Decimals(totalFinalCost - totalPaid);

			const updatedBulk = {
				...bulk,
				bulk_group: groupId,
				paid_amount: totalPaid,
				remaining_balance: Math.max(0, totalRemaining),
				dental_payment_history: [...(bulk?.dental_payment_history || []), payment],
			};

			// 👇 distribute paid amount across children (even split)
			const children = treatmentPlans.filter(p => p.bulk_group === groupId);
			const perChildPaid = formatTo2Decimals(totalPaid / children.length);

			const updatedPlans = treatmentPlans.map(plan => {
				if (plan.bulk_group === groupId) {
					const finalCostPerTooth = formatTo2Decimals(parseFloat(plan.final_cost) || 0);

					const cappedPaid = Math.min(perChildPaid, finalCostPerTooth);
					const newRemaining = formatTo2Decimals(Math.max(0, finalCostPerTooth - cappedPaid));
					return {
						...plan,
						paid_amount: cappedPaid,
						remaining_balance: newRemaining,
						treatment_status: newRemaining <= 0 ? "completed" : plan.treatment_status,
						dental_payment_history: [...(plan.dental_payment_history || []), payment],
					};
				}
				return plan;
			});

			setTreatmentPlans(updatedPlans);
			setBulkPayments(prev => ({ ...prev, [groupId]: updatedBulk }));
			setShowAddPaymentForm(false);
			syncParent({ plans: updatedPlans, bulkPayments: updatedBulk });
		} else {
			const updatedPlans = treatmentPlans.map(plan => {
				if (plan.treatment_id === selectedTreatment.treatment_id) {
					const prevPaid = parseFloat(plan.paid_amount) || 0;
					const newPayment = parseFloat(payment.amount) || 0;
					const totalPaid = formatTo2Decimals(prevPaid + newPayment);

					const finalCost = formatTo2Decimals(parseFloat(plan.final_cost) || parseFloat(plan.cost) || 0);
		            const remainingBalance = formatTo2Decimals(finalCost - totalPaid);
					const newStatus = remainingBalance <= 0 ? "completed" : plan.treatment_status === "planned" ? "in-progress" : plan.treatment_status;

					return {
						...plan,
						paid_amount: totalPaid,
						remaining_balance: Math.max(0, remainingBalance),
						treatment_status: newStatus,
						dental_payment_history: [...(plan.dental_payment_history || []), payment]
					};
				}
				return plan;
			});

			setTreatmentPlans(updatedPlans);
			setShowAddPaymentForm(false);
			syncParent({ plans: updatedPlans });
		}
	};

	const getCondition = (toothNumber) => {
		const plans = treatmentPlans
			.filter(p => p.tooth_number === toothNumber && p.treatment_status === 'completed')
			.sort((a, b) => new Date(b.treatment_date) - new Date(a.treatment_date));
		return plans.length > 0 ? plans[0].condition : 'healthy';
	};

	const toggleExpandPayments = (treatment_id) => {
		setExpandedPayments(prev => ({
			...prev,
			[treatment_id]: !prev[treatment_id]
		}));
	};

	const teethData = getTeethData(chartType);

	const formatCurrency = (val) => {
		const num = Number(val);
		return isNaN(num) ? "0.00" : num.toFixed(2);
	};

	const renderSingleTreatmentRow = (plan) => {
		return (
			<React.Fragment key={plan.treatment_id}>
				<tr>
					<td className="fw-bold">{plan.tooth_number}</td>
					<td>
						<Badge bg="secondary">{plan.condition}</Badge>
					</td>
					<td>
						{plan.bulk_group ? (
							<Badge bg="info">Bulk</Badge>
						) : (
							<Badge bg="secondary">Single</Badge>
						)}
					</td>
					<td>{plan.treatment_type}</td>
					<td>{formatDisplayDate(plan.treatment_date)}</td>
					<td>₹{(parseFloat(plan.original_cost) || parseFloat(plan.cost) || 0).toFixed(2)}</td>
					<td>
						₹{(parseFloat(plan.final_cost) || parseFloat(plan.cost) || 0).toFixed(2)}
						{parseFloat(plan.discount_amount || 0) > 0 && (
							<div>
								<small className="text-success">
									(-₹{parseFloat(plan.discount_amount || 0).toFixed(2)} discount)
								</small>
							</div>
						)}
					</td>
					<td>
						<Badge bg="success">
							₹{(parseFloat(plan.paid_amount) || 0).toFixed(2)}
						</Badge>
					</td>
					<td>
						{parseFloat(plan.remaining_balance || 0) > 0 ? (
							<Badge bg="warning">
								₹{parseFloat(plan.remaining_balance || 0).toFixed(2)}
							</Badge>
						) : (
							<Badge bg="success">Paid</Badge>
						)}
					</td>
					<td>
						<Badge
							bg={
								plan.treatment_status === "completed" ? "success" : plan.treatment_status === "in-progress" ? "warning" : "info"
							}
						>
							{plan.treatment_status}
						</Badge>
					</td>
					<td>{plan.note}</td>
					<td>
						<Card className='border-0' style={{ backgroundColor: 'transparent' }}>
							<Row className="g-2">
								<Col xs={6}>
									{(plan.isNew || plan.treatment_status !== "completed") && (
										<Button className="w-100" size="sm" variant="outline-primary" onClick={() => handleEditTreatmentPlan(plan)}>
											Edit
										</Button>
									)}
								</Col>
								<Col xs={6}>
									{plan.isNew && (
										<Button className="w-100" size="sm" variant="outline-danger" onClick={() => handleRemoveTreatmentPlan(plan.treatment_id)}>
											Remove
										</Button>
									)}
								</Col>
								<Col xs={6}>
									{!plan.isNew && plan.treatment_status !== "completed" && plan.remaining_balance > 0 && (
										<Button className="w-100" size="sm" variant="outline-primary" onClick={() => handleOpenAddPaymentForm(plan)}>
											Part Pay
										</Button>
									)}
								</Col>
								<Col xs={6}>
									<Button className="w-100" size="sm" variant="outline-secondary" onClick={() => toggleExpandPayments(plan.treatment_id)}>
										{expandedPayments[plan.treatment_id] ? "Hide Pay" : "View Pay"}
									</Button>
								</Col>
							</Row>
						</Card>

					</td>
				</tr>
				{expandedPayments[plan.treatment_id] && (
					<tr>
						<td colSpan="11">
							<Table size="sm" bordered>
								<thead>
									<tr>
										<th>#</th>
										<th>Invoice Number</th>
										<th>Date</th>
										<th>Amount</th>
										<th>Method</th>
									</tr>
								</thead>
								<tbody>
									{plan.dental_payment_history && plan.dental_payment_history.length > 0 ? (
										plan.dental_payment_history.map((payment, index) => (
											<tr key={index}>
												<td>{index + 1}</td>
												<td>{payment.invoice_number || ""}</td>
												<td>{formatDisplayDate(payment.date)}</td>
												<td>₹{parseFloat(payment.amount).toFixed(2)}</td>
												<td>{amountPaidVia[payment.amount_paid_via]}</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan="5" className="text-center">No payments recorded</td>
										</tr>
									)}
								</tbody>
							</Table>
						</td>
					</tr>
				)}
			</React.Fragment>
		);
	};

	const renderBulkGroupRows = (group) => {
		const first = group.items[0];
		const bulkPaymentData = bulkPayments[group.bulk_group];

		// 🔑 Fallback calculation from child items
		const aggregated = group.items.reduce(
			(acc, item) => {
				const orig = parseFloat(item.original_cost || item.cost || 0);
				const final = parseFloat(item.final_cost || item.cost || 0);
				const paid = parseFloat(item.paid_amount || 0);
				acc.original_cost += orig;
				acc.final_cost += final;
				acc.paid_amount += paid;
				return acc;
			},
			{ original_cost: 0, final_cost: 0, paid_amount: 0 }
		);
		aggregated.remaining_balance = aggregated.final_cost - aggregated.paid_amount;

		// Prefer bulkPaymentData if exists, otherwise use aggregated
		const summary = bulkPaymentData || aggregated;

		// 🦷 Collect all teeth numbers in one place
		const teethList = group.items.map((p) => p.tooth_number).sort((a, b) => a - b);
		const teethDisplay = teethList.length > 10 ? `${teethList.slice(0, 10).join(", ")} ... (+${teethList.length - 10} more)` : teethList.join(", ");

		return (
			<React.Fragment key={group.bulk_group}>
				{/* Bulk header row */}
				<tr style={{ backgroundColor: "#f0f9ff" }}>
					<td colSpan={4} className="fw-bold">
						<div>
							<div>
								🦷 {first.treatment_type} Bulk Treatment
							</div>
							<small className="text-muted">
								Teeth: {teethDisplay} ({group.items.length} total)
							</small>
						</div>
					</td>
					<td>{formatDisplayDate(first.treatment_date)}</td>
					<td>₹{formatCurrency(summary.original_cost)}</td>
					<td>₹{formatCurrency(summary.final_cost)}</td>
					<td><Badge bg="success">₹{formatCurrency(summary.paid_amount)}</Badge></td>
					<td>
						{summary.remaining_balance > 0 ? (
							<Badge bg="warning">₹{formatCurrency(summary.remaining_balance)}</Badge>
						) : (
							<Badge bg="success">Paid</Badge>
						)}
					</td>
					<td>
						<Badge bg={first.treatment_status === "completed" ? "success" : first.treatment_status === "in-progress" ? "warning" : "info"}>
							{first.treatment_status}
						</Badge>
					</td>
					<td>{first.note}</td>
					<td>
						<Card className='border-0' style={{ backgroundColor: 'transparent' }}>
							<Row className="g-2">
								<Col xs={6}>
									{(first.isNew || first.treatment_status !== "completed") && (
										<Button className="w-100" size="sm" variant="outline-primary" onClick={() => handleEditTreatmentPlan(first, bulkPaymentData, group.items.length)}>
											Edit
										</Button>
									)}
								</Col>
								<Col xs={6}>
									{first.isNew && (
										<Button className="w-100" size="sm" variant="outline-danger" onClick={() => handleRemoveBulkTreatment(group.bulk_group)}>
											Remove
										</Button>
									)}
								</Col>
								<Col xs={6}>
									{!first.isNew && summary.remaining_balance > 0 && (
										<Button className="w-100" size="sm" variant="outline-primary" onClick={() => handleOpenAddPaymentForm(
											{
												bulk_group: group.bulk_group,
												remaining_balance: summary.remaining_balance,
												isBulk: true
											},
											group.bulk_group
										)}>
											Part Pay
										</Button>
									)}
								</Col>
								<Col xs={6}>
									<Button className="w-100" size="sm" variant="outline-secondary" onClick={() => toggleExpandPayments(group.bulk_group)}>
										{expandedPayments[group.bulk_group] ? "Hide Pay" : "View Pay"}
									</Button>
								</Col>
							</Row>
						</Card>
					</td>
				</tr>

				{/* Expanded payments */}
				{expandedPayments[group.bulk_group] && (
					<tr>
						<td colSpan="11">
							<Table size="sm" bordered>
								<thead>
									<tr>
										<th>#</th>
										<th>Invoice Number</th>
										<th>Date</th>
										<th>Amount</th>
										<th>Method</th>
									</tr>
								</thead>
								<tbody>
									{(bulkPayments[group.bulk_group]?.dental_payment_history || []).length > 0 ? (
										(bulkPayments[group.bulk_group].dental_payment_history).map((payment, index) => (
											<tr key={index}>
												<td>{index + 1}</td>
												<td>{payment.invoice_number || ""}</td>
												<td>{formatDisplayDate(payment.date)}</td>
												<td>₹{formatCurrency(payment.amount)}</td>
												<td>{amountPaidVia[payment.amount_paid_via]}</td>
											</tr>
										))
									) : (
										<tr>
											<td colSpan="5" className="text-center"> No payments recorded</td>
										</tr>
									)}
								</tbody>
							</Table>
						</td>
					</tr>
				)}
			</React.Fragment>
		);
	};

	const groupPlans = (plans) => {
		const groups = {};
		plans.forEach(p => {
			const key = p.bulk_group || `single-${p.treatment_id}`;
			if (!groups[key]) {
				groups[key] = { bulk_group: p.bulk_group, items: [] };
			}
			groups[key].items.push(p);
		});
		return Object.values(groups);
	};

	const getDisplayableTreatments = () => {
		const grouped = groupPlans(treatmentPlans);

		let displayRows = [];
		grouped.forEach(group => {
			if (group.bulk_group) {
				displayRows.push({ type: "bulk", group });
			} else {
				group.items.forEach(plan => displayRows.push({ type: "single", plan }));
			}
		});

		// 🕒 Sort all treatments by date descending
		displayRows.sort((a, b) => {
			const dateA =
				a.type === "bulk"
					? new Date(a.group.items[0]?.treatment_date || 0)
					: new Date(a.plan?.treatment_date || 0);
			const dateB =
				b.type === "bulk"
					? new Date(b.group.items[0]?.treatment_date || 0)
					: new Date(b.plan?.treatment_date || 0);
			return dateB - dateA; // newest first
		});

		return displayRows;
	};

	const [totalCount, setTotalCount] = useState(0);
	const displayableRows = getDisplayableTreatments();
	const indexOfLast = currentPage * pageSize;
	const indexOfFirst = indexOfLast - pageSize;
	const currentDisplayRows = displayableRows.slice(indexOfFirst, indexOfLast);

	// ✅ Keep total count & pagination synced
	useEffect(() => {
		const groupedRows = getDisplayableTreatments();
		setTotalCount(groupedRows.length);

		const totalPages = Math.ceil(groupedRows.length / pageSize);
		if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
	}, [treatmentPlans, pageSize]);

	return (
		<div>
			<div className="chart-header mb-3">
				<h3 className="text-center">{getChartTitle(chartType)}</h3>
				<p className="text-center text-muted">{getChartDescription(chartType)}</p>

				{/* Treatment Plan Mode Toggle */}
				<div className="text-center mb-3">
					<ToggleButtonGroup
						type="radio"
						name="treatmentMode"
						value={bulkMode ? "bulk" : "single"}
						onChange={(val) => {
							if (val === "bulk" && !bulkMode) toggleBulkMode();
							if (val === "single" && bulkMode) toggleBulkMode();
						}}
					>
						<ToggleButton id="singleMode" value="single" variant="outline-primary">
							<Cursor className="me-1" />
							Single Tooth Treatment
						</ToggleButton>

						<ToggleButton id="bulkMode" value="bulk" variant="outline-primary">
							<Collection className="me-1" />
							Bulk Treatment Planning
						</ToggleButton>
					</ToggleButtonGroup>
				</div>

				{bulkMode && (
					<Alert variant="info" className="text-center">
						<strong>Bulk Mode Active:</strong> Click multiple teeth to select them
						for bulk treatment planning.
						{selectedTeeth.size > 0 && (
							<span className="ms-2">
								<Badge bg="primary">{selectedTeeth.size} teeth selected</Badge>
							</span>
						)}
					</Alert>
				)}
			</div>

			{/* Quadrant Layout */}
			<div>
				<div className="row mb-3">
					{/* Upper Right Quadrant */}
					<div className="col-md-6">
						<div className="quadrant-section">
							<h6 className="text-center mb-3">Upper Right</h6>
							<div className="d-flex justify-content-center flex-wrap">
								{teethData.upper.slice(0, Math.floor(teethData.upper.length / 2)).map((tooth) => {
									const identifier = tooth.number;
									const isSelected = bulkMode ? selectedTeeth.has(identifier) : selectedTooth === identifier;
									return (
										<div key={identifier} className="tooth-wrapper">
											<Tooth
												tooth={tooth}
												condition={getCondition(tooth.number)}
												isSelected={isSelected}
												bulkMode={bulkMode}
												onClick={handleToothClick}
												onAddTreatmentPlan={handleAddTreatmentPlan}
											/>
										</div>
									);
								})}
							</div>
						</div>
					</div>

					{/* Upper Left Quadrant */}
					<div className="col-md-6">
						<div className="quadrant-section">
							<h6 className="text-center mb-3">Upper Left</h6>
							<div className="d-flex justify-content-center flex-wrap">
								{teethData.upper.slice(Math.floor(teethData.upper.length / 2)).map((tooth) => {
									const identifier = tooth.number;
									const isSelected = bulkMode ? selectedTeeth.has(identifier) : selectedTooth === identifier;
									return (
										<div key={identifier} className="tooth-wrapper">
											<Tooth
												tooth={tooth}
												condition={getCondition(tooth.number)}
												isSelected={isSelected}
												bulkMode={bulkMode}
												onClick={handleToothClick}
												onAddTreatmentPlan={handleAddTreatmentPlan}
											/>
										</div>
									);
								})}
							</div>
						</div>
					</div>
				</div>

				<div className="row">
					{/* Lower Right Quadrant */}
					<div className="col-md-6">
						<div className="quadrant-section">
							<h6 className="text-center mb-3">Lower Right</h6>
							<div className="d-flex justify-content-center flex-wrap">
								{teethData.lower.slice(Math.floor(teethData.lower.length / 2)).reverse().map((tooth) => {
									const identifier = tooth.number;
									const isSelected = bulkMode ? selectedTeeth.has(identifier) : selectedTooth === identifier;
									return (
										<div key={identifier} className="tooth-wrapper">
											<Tooth
												tooth={tooth}
												condition={getCondition(tooth.number)}
												isSelected={isSelected}
												bulkMode={bulkMode}
												onClick={handleToothClick}
												onAddTreatmentPlan={handleAddTreatmentPlan}
											/>
										</div>
									);
								})}
							</div>
						</div>
					</div>

					{/* Lower Left Quadrant */}
					<div className="col-md-6">
						<div className="quadrant-section">
							<h6 className="text-center mb-3">Lower Left</h6>
							<div className="d-flex justify-content-center flex-wrap">
								{teethData.lower.slice(0, Math.floor(teethData.lower.length / 2)).reverse().map((tooth) => {
									const identifier = tooth.number;
									const isSelected = bulkMode ? selectedTeeth.has(identifier) : selectedTooth === identifier;
									return (
										<div key={identifier} className="tooth-wrapper">
											<Tooth
												tooth={tooth}
												condition={getCondition(tooth.number)}
												isSelected={isSelected}
												bulkMode={bulkMode}
												onClick={handleToothClick}
												onAddTreatmentPlan={handleAddTreatmentPlan}
											/>
										</div>
									);
								})}
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* Bulk Treatment Plan Button */}
			{bulkMode && selectedTeeth.size > 0 && (
				<div className="text-center mb-3">
					<Button variant="success" size="lg" onClick={handleBulkAddTreatmentPlan}>
						<PlusCircle className="me-2" />
						Create Treatment Plans for {selectedTeeth.size} Selected Teeth
					</Button>
				</div>
			)}

			{/* Treatment Plans Table */}
			{treatmentPlans.length > 0 && (
				<div className="treatment-plans-section mt-3 mb-3">
					<h5 className="mb-3">Treatment Plans</h5>
					<Table bordered hover responsive>
						<thead light="true">
							<tr>
								<th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Tooth</th>
								<th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Condition</th>
								<th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Type</th>
								<th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Treatment</th>
								<th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Date</th>
								<th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Original Cost</th>
								<th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Final Cost</th>
								<th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Paid</th>
								<th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Balance</th>
								<th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Status</th>
								<th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Notes</th>
								<th style={{ verticalAlign: 'text-top', textAlign: 'center' }}>Action</th>
							</tr>
						</thead>
						<tbody>
							{currentDisplayRows.map((row) =>
								row.type === "bulk" ? renderBulkGroupRows(row.group) : renderSingleTreatmentRow(row.plan)
							)}
						</tbody>
					</Table>

					<CustomPagination
						totalCount={totalCount}
						currentPage={currentPage}
						pageSize={pageSize}
						onPageChange={setCurrentPage} />
				</div>
			)}

			{/* Chart Legend */}
			<div className="dental-chart-legend mt-3">
				<h6>Legend</h6>
				<div className="row">
					{toothConditions.map(({ value, label, color }) => (
						<div className="col-md-4" key={value}>
							<div className="legend-item d-flex align-items-center mb-2">
								<div
									className="legend-color"
									style={{
										width: '20px',
										height: '20px',
										backgroundColor: color,
										border: color === '#ffffff' || color === '#e9ecef' ? '1px solid #333' : 'none',
										marginRight: '10px'
									}}
								></div>
								<span>{label}</span>
							</div>
						</div>
					))}
				</div>
			</div>

			{/* Treatment Plan Form Modal */}
			{showTreatmentForm && selectedTooth && (
				<TreatmentPlanForm
					show={showTreatmentForm}
					toothNumber={selectedTooth}
					initialCondition={getCondition(selectedTooth)}
					onSave={handleSaveTreatmentPlan}
					onCancel={() => setShowTreatmentForm(false)}
				/>
			)}

			{/* Edit Treatment Plan Form Modal */}
			{showEditTreatmentForm && editingPlan && (
				<EditTreatmentPlanForm
					show={showEditTreatmentForm}
					treatmentPlan={editingPlan}
					onSave={handleUpdateTreatmentPlan}
					onCancel={() => { setShowEditTreatmentForm(false); setEditingPlan(null); }}
				/>
			)}

			{/* Bulk Treatment Plan Form Modal */}
			{showBulkTreatmentForm && selectedTeeth.size > 0 && (
				<BulkTreatmentPlanForm
					show={showBulkTreatmentForm}
					selectedTeeth={Array.from(selectedTeeth)}
					initialCondition={getCondition(Array.from(selectedTeeth)[0])}
					onSave={handleSaveBulkTreatmentPlan}
					onCancel={() => {
						setShowBulkTreatmentForm(false);
						setSelectedTeeth(new Set());
						setBulkMode(false);
					}}
				/>
			)}

			{/* Edit Bulk Treatment Form Modal */}
			{showEditBulkTreatmentForm && editingBulk && (
				<EditBulkTreatmentForm
					show={showEditBulkTreatmentForm}
					bulkTreatment={{
						...editingBulk,
						teeth: treatmentPlans
							.filter(p => p.bulk_group === editingBulk.bulk_group)
							.map(p => p.tooth_number)
					}}
					onSave={handleUpdateBulkTreatment}
					onCancel={() => { setShowEditBulkTreatmentForm(false); setEditingBulk(null); }}
				/>
			)}

			{/* Add Dental Payment Form Modal */}
			{showAddPaymentForm && selectedTreatment && (
				<AddDentalPaymentForm
					show={showAddPaymentForm}
					treatmentPlan={selectedTreatment}
					onSave={handleSavePayment}
					onCancel={() => setShowAddPaymentForm(false)}
				/>
			)}
		</div>
	);
};

export default DentalChart;
